/**
 * Validation helper functions for forms
 */

// Email validation regex (RFC 5322 standard)
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

// Password strength regex - includes common special characters
const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#+\-=<>{}()[\]|\\:";'.,_`~])[A-Za-z\d@$!%*?&#+\-=<>{}()[\]|\\:";'.,_`~]{8,}$/;

// Common business email domains to check against
const BUSINESS_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'icloud.com',
  'aol.com',
  'mail.com',
  'protonmail.com',
];

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

/**
 * Validate email format and business suitability
 */
export const validateEmail = (email: string): ValidationResult => {
  if (!email) {
    return { isValid: false, message: 'Email is required' };
  }

  if (!EMAIL_REGEX.test(email)) {
    return { isValid: false, message: 'Please enter a valid email address' };
  }

  // Check if it's a business email (not personal)
  const domain = email.split('@')[1]?.toLowerCase();
  if (domain && BUSINESS_EMAIL_DOMAINS.includes(domain)) {
    return {
      isValid: true, // Still valid, but show warning
      message: 'Consider using your business email for better team collaboration',
    };
  }

  return { isValid: true };
};

/**
 * Check if email appears to be a business email
 */
export const isBusinessEmail = (email: string): boolean => {
  const domain = email.split('@')[1]?.toLowerCase();
  return domain ? !BUSINESS_EMAIL_DOMAINS.includes(domain) : false;
};

/**
 * Validate password strength
 */
export const validatePassword = (password: string): ValidationResult => {
  if (!password) {
    return { isValid: false, message: 'Password is required' };
  }

  if (password.length < 8) {
    return { isValid: false, message: 'Password must be at least 8 characters long' };
  }

  if (!PASSWORD_REGEX.test(password)) {
    return {
      isValid: false,
      message: 'Password must contain uppercase, lowercase, number, and special character',
    };
  }

  return { isValid: true };
};

/**
 * Validate password confirmation
 */
export const validatePasswordConfirmation = (
  password: string,
  confirmPassword: string
): ValidationResult => {
  if (!confirmPassword) {
    return { isValid: false, message: 'Please confirm your password' };
  }

  if (password !== confirmPassword) {
    return { isValid: false, message: 'Passwords do not match' };
  }

  return { isValid: true };
};

/**
 * Validate company name
 */
export const validateCompanyName = (companyName: string): ValidationResult => {
  if (!companyName || companyName.trim().length === 0) {
    return { isValid: false, message: 'Company name is required' };
  }

  if (companyName.trim().length < 2) {
    return { isValid: false, message: 'Company name must be at least 2 characters long' };
  }

  if (companyName.length > 100) {
    return { isValid: false, message: 'Company name must be less than 100 characters' };
  }

  return { isValid: true };
};

/**
 * Validate name fields (first name, last name)
 */
export const validateName = (name: string, fieldName: string = 'Name'): ValidationResult => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, message: `${fieldName} is required` };
  }

  if (name.trim().length < 2) {
    return { isValid: false, message: `${fieldName} must be at least 2 characters long` };
  }

  if (name.length > 50) {
    return { isValid: false, message: `${fieldName} must be less than 50 characters` };
  }

  // Check for invalid characters (allow letters, spaces, hyphens, apostrophes)
  const nameRegex = /^[a-zA-Z\s\-']+$/;
  if (!nameRegex.test(name)) {
    return {
      isValid: false,
      message: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`,
    };
  }

  return { isValid: true };
};

/**
 * Get password strength indicator
 */
export const getPasswordStrength = (
  password: string
): {
  score: number;
  label: string;
  color: string;
} => {
  if (!password) return { score: 0, label: 'No password', color: 'gray' };

  let score = 0;

  // Length check
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;

  // Character variety checks
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[@$!%*?&#+\-=<>{}()[\]|\\:";'.,_`~]/.test(password)) score += 1;

  // Complexity bonus
  if (password.length >= 16 && score >= 5) score += 1;

  const strengthMap = {
    0: { label: 'Very Weak', color: 'red' },
    1: { label: 'Very Weak', color: 'red' },
    2: { label: 'Weak', color: 'orange' },
    3: { label: 'Fair', color: 'yellow' },
    4: { label: 'Good', color: 'blue' },
    5: { label: 'Strong', color: 'green' },
    6: { label: 'Very Strong', color: 'green' },
    7: { label: 'Excellent', color: 'green' },
  };

  return {
    score,
    label: strengthMap[score as keyof typeof strengthMap].label,
    color: strengthMap[score as keyof typeof strengthMap].color,
  };
};
